import { Button, Card, Divider, Image, Link, makeStyles, Skeleton, SkeletonItem, Text, tokens } from '@fluentui/react-components';
import { ArrowDownloadFilled, ChevronDownFilled, ChevronRightFilled } from "@fluentui/react-icons";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ValidationResults, ValidationStatus } from "../abstractions/EAppValidation";
import { IAppValidationItem, IAppValidationTestCase } from "../abstractions/IAppValidation";
import { ValidationTestCaseResultIcon } from "./ValidationTestCaseResultIcon";
import { useCommand } from '@tdp/core';

const useStyles = makeStyles({
  card: {
    padding: "0px",
    gap: "0px",
    height: "auto",
    minHeight: "400px"
  },
  cardRows: {
    overflowY: "auto",
  },
  inProgress: {
    display: 'flex',
    alignItems: 'center',
    padding: '64px 0px',
  },
  cardSkeleton: {
    gap: '18px',
  },
  container: {
    borderTop: `2px solid ${tokens.colorNeutralBackground3}`
  },
  row: {
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    flexGrow: 1,
    minHeight: "32px",
    height: "auto",
    paddingTop: "6px",
    paddingBottom: "6px",
    alignItems: "center"
  },
  subRow: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    paddingLeft: "10%",
    minHeight: "32px",
    height: "auto",
    display: 'flex',
    alignItems: 'center',
    paddingTop: "3px",
    paddingBottom: "3px",
  },
  icons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '5%',
  },
  message: {
    width: '66%',
  },
  linkContainer: {
    width: '12%',
    padding: '0px 6px'
  },
})

export const ValidationDetails: React.FC<{
  currentValidation: IAppValidationItem | null;
}> = ({ currentValidation }) => {
  const classes = useStyles();
  const intl = useIntl();

  if (!currentValidation) {
    return <ValidationSkeleton />
  }

  if (currentValidation.status === ValidationStatus.Completed) {
    return (
      <Card className={classes.card}>
        <ValidationdationDetailsActions currentValidation={currentValidation} />
        <ValidationDetailsHeader />
        <div className={classes.cardRows}>
          <ValidationDetailRowRenderer validation={currentValidation?.validationResults?.successes} result={ValidationResults.Success} />
          <ValidationDetailRowRenderer validation={currentValidation?.validationResults?.warnings} result={ValidationResults.Warning} />
          <ValidationDetailRowRenderer validation={currentValidation?.validationResults?.failures} result={ValidationResults.Error} />
          <ValidationDetailRowRenderer validation={currentValidation?.validationResults?.skipped} result={ValidationResults.Skipped} />
        </div>
      </Card>
    )
  }

  const validationInProgressText = intl.formatMessage({
    defaultMessage: 'While we are working on validating your app, you can keep working on other tasks in Developer Portal without any interruption. Please check back after some time to see the status of your app validation.',
    description: "Validation in progress"
  });

  return (
    <ValidationInProgress
      message={validationInProgressText}
      title='Validation in Progress'
      src='/images/illustrations/validation-wait.png'
    />
  )
}

const ValidationSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <Card className={classes.cardSkeleton}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <SkeletonItem style={{ height: '16px', width: '100%' }} />
        <SkeletonItem style={{ height: '16px', width: '60%' }} />
        <SkeletonItem style={{ height: '16px', width: '40%' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <SkeletonItem style={{ height: '16px', width: '100%' }} />
        <SkeletonItem style={{ height: '16px', width: '60%' }} />
        <SkeletonItem style={{ height: '16px', width: '40%' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <SkeletonItem style={{ height: '16px', width: '100%' }} />
        <SkeletonItem style={{ height: '16px', width: '60%' }} />
        <SkeletonItem style={{ height: '16px', width: '40%' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <SkeletonItem style={{ height: '16px', width: '100%' }} />
        <SkeletonItem style={{ height: '16px', width: '60%' }} />
        <SkeletonItem style={{ height: '16px', width: '40%' }} />
      </div>
    </Card>)
};

export const ValidationInProgress: React.FC<{
  title: string;
  src: string;
  message: string;
}> = ({ title, src, message }) => {
  const classes = useStyles();
  return (
    <Card className={classes.inProgress}>
      <Image src={src} alt={title} width={200} height={200} />
      <Text weight="bold">
        {title}
      </Text>
      <Text style={{ maxWidth: "650px" }}>
        {message}
      </Text>
    </Card>
  );
}

const ValidationdationDetailsActions: React.FC<{ currentValidation: IAppValidationItem | undefined }> = ({ currentValidation }) => {
  const classes = useStyles();
  const totalItems = (currentValidation?.validationResults?.failures?.length ?? 0)
    + (currentValidation?.validationResults?.warnings?.length ?? 0)
    + (currentValidation?.validationResults?.successes?.length ?? 0)
    + (currentValidation?.validationResults?.skipped?.length ?? 0)

  const exportDetails = useCommand(s => s.appValidationCommands.exportDetails);

  const onDownloadReport = () => {
    currentValidation?.appValidationId && exportDetails({
      validationId: currentValidation?.appValidationId,
      ext: 'csv'
    })
  }

  const onDownloadAppPackage = () => {
    currentValidation?.appValidationId && exportDetails({
      validationId: currentValidation?.appValidationId,
      ext: 'zip'
    })
  }

  return (
    <div className={classes.row}>
      <div style={{ gap: '6px', display: 'flex' }}>
        <Button appearance='transparent' icon={<ArrowDownloadFilled />} onClick={onDownloadAppPackage}>
          <FormattedMessage
            defaultMessage={`Download app package`}
            description={`Download the app package`}
          />
        </Button>
        <Divider vertical style={{ padding: '0px' }} />
        <Button appearance='transparent' icon={<ArrowDownloadFilled />} onClick={onDownloadReport}>
          <FormattedMessage
            defaultMessage={`Download Report`}
            description={`Download the validation report`}
          />
        </Button>
        <Divider vertical style={{ padding: '0px' }} />
        <Text weight='bold' style={{ alignItems: 'center', display: 'flex', marginLeft: '12px' }}>
          <FormattedMessage
            defaultMessage={'{item} items'}
            description={`Number of items in the list`}
            values={{ item: totalItems }}
          />
        </Text>
      </div>
    </div>
  )
}

const ValidationDetailsHeader: React.FC = () => {
  const classes = useStyles();
  const intl = useIntl();
  const chevron = intl.formatMessage({ defaultMessage: "Expand or Collapse Button" })
  const status = intl.formatMessage({ defaultMessage: "Status" })
  const testCase = intl.formatMessage({ defaultMessage: "Test Case" })
  const policy = intl.formatMessage({ defaultMessage: "Policy number" })
  const guideline = intl.formatMessage({ defaultMessage: "Guidelines" })

  return (
    <div className={classes.container}>
      <div className={classes.row} >
        <div className={classes.icons} aria-label={chevron}></div>
        <div className={classes.icons} aria-label={status}></div>
        <div className={classes.message}><b>{testCase}</b></div>
        <div className={classes.linkContainer}><b>{policy}</b></div>
        <div className={classes.linkContainer}><b>{guideline}</b></div>
      </div>
    </div>
  )
}

export const ValidationDetailRowRenderer: React.FC<{
  validation: IAppValidationTestCase[] | undefined;
  result: ValidationResults;
}> = ({ validation, result }) => {
  return (
    <div>
      {validation?.map((testCase, index) => (
        <ValidationDetailsRow key={index} testCase={testCase} index={index} result={result} />
      ))}
    </div>
  );
};


const ValidationDetailsRow: React.FC<{
  testCase: IAppValidationTestCase;
  index: number;
  result: ValidationResults;
}> = ({ testCase, index, result }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);
  const message = testCase.message
  const recommendation = testCase.artifacts?.recommendation ?? null
  const policy = testCase.artifacts?.policyNumber ?? null
  const guideline = testCase.artifacts?.docsUrl ?? null
  const aria = `${testCase.title} ${result}`

  return (
    <div className={classes.container} key={index} role="region" aria-label={aria}>
      <div className={classes.row}>
        <div className={classes.icons}>
          {recommendation &&
            <Button
              appearance="transparent"
              icon={expanded ? <ChevronDownFilled /> : <ChevronRightFilled />}
              onClick={() => setExpanded(!expanded)}
            />
          }
        </div>
        <div className={classes.icons}>
          <ValidationTestCaseResultIcon result={result} />
        </div>
        <div className={classes.message}>
          <Text>{message}</Text>
        </div>
        <div className={classes.linkContainer}>
          {policy ?
            <Link href={testCase.artifacts?.policyLinkUrl ?? "#"} target="_blank">
              <Text>
                {policy}
              </Text>
            </Link>
            : <Text>
              <FormattedMessage
                defaultMessage={`N/A`}
                description={`Display text for not available`}
              />
            </Text>
          }
        </div>
        <div className={classes.linkContainer}>
          {
            guideline ?
              <Link href={guideline} target="_blank">
                <FormattedMessage
                  defaultMessage={`View`}
                  description={`View Guideline text`}
                />
              </Link>
              : <Text>
                <FormattedMessage
                  defaultMessage={`N/A`}
                  description={`Display text for not available`}
                />
              </Text>
          }
        </div>
      </div>
      {expanded && recommendation &&
        <div className={classes.subRow}>
          <Text>{recommendation}</Text>
        </div>
      }
    </div>
  )
}
